import apiClient from './index';
import {
  ConsumptionReportResponse,
  SettlementElectricityHourlyReportResponse,
  SettlementElectricityReportResponse,
} from './types';

export type ResidentialConsumptionReportParams = {
  residentialComplexId: string;
  period?: string | null;
  anchorDay?: number | null;
  fallbackDays?: number | null;
  periodShift?: number | null;
  buildingId?: string | null;
  corpsId?: string | null;
  apartmentId?: string | null;
};

export type SettlementElectricityReportParams = {
  residentialComplexId: string;
  dateFrom: string;
  dateTo: string;
  buildingId?: string | null;
  fallbackDays?: number | null;
};

export type SettlementElectricityHourlyReportParams = {
  residentialComplexId: string;
  deviceId: string;
  dateFrom: string;
  dateTo: string;
  buildingId?: string | null;
  skip?: number;
  limit?: number;
};

export type CalculationReportArtifact = {
  id: string;
  artifact_type: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
};

export type CalculationReportRevision = {
  id: string;
  building_id: string;
  heat_period_id?: string | null;
  parent_revision_id?: string | null;
  calculation_type: 'STANDARD' | 'HEAT_ALLOCATOR';
  period_start: string;
  period_end: string;
  revision: number;
  status: 'OPEN' | 'FINALIZED' | 'CORRECTION';
  finalized_at?: string | null;
  unlock_reason?: string | null;
  artifacts: CalculationReportArtifact[];
};

const buildParams = (params: ResidentialConsumptionReportParams) => {
  const query: Record<string, string | number> = {};
  if (params.period) query.period = params.period;
  if (typeof params.anchorDay === 'number') query.anchor_day = params.anchorDay;
  if (typeof params.fallbackDays === 'number') query.fallback_days = params.fallbackDays;
  if (typeof params.periodShift === 'number') query.period_shift = params.periodShift;
  if (params.buildingId) query.building_id = params.buildingId;
  if (params.corpsId) query.corps_id = params.corpsId;
  if (params.apartmentId) query.apartment_id = params.apartmentId;
  return query;
};

const buildSettlementParams = (params: SettlementElectricityReportParams) => {
  const query: Record<string, string | number> = {
    date_from: params.dateFrom,
    date_to: params.dateTo,
  };
  if (params.buildingId) query.building_id = params.buildingId;
  if (typeof params.fallbackDays === 'number') query.fallback_days = params.fallbackDays;
  return query;
};

const buildSettlementHourlyParams = (params: SettlementElectricityHourlyReportParams) => {
  const query: Record<string, string | number> = {
    device_id: params.deviceId,
    date_from: params.dateFrom,
    date_to: params.dateTo,
  };
  if (params.buildingId) query.building_id = params.buildingId;
  if (typeof params.skip === 'number') query.skip = params.skip;
  if (typeof params.limit === 'number') query.limit = params.limit;
  return query;
};

const parseContentDisposition = (headers: Record<string, unknown>): string | null => {
  const rawDisposition =
    (headers['content-disposition'] as unknown) ??
    (headers['Content-Disposition'] as unknown);
  const disposition = Array.isArray(rawDisposition)
    ? rawDisposition[0]
    : typeof rawDisposition === 'string'
    ? rawDisposition
    : null;
  if (!disposition) return null;
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (match && match[1]) return decodeURIComponent(match[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain && plain[1] ? plain[1] : null;
};

export const reportsApi = {
  async getCalculationRevisions(
    buildingId: string,
    calculationType?: 'STANDARD' | 'HEAT_ALLOCATOR',
  ): Promise<CalculationReportRevision[]> {
    const response = await apiClient.get<{ items: CalculationReportRevision[] }>(
      `/reports/calculations/buildings/${buildingId}/revisions`,
      { params: calculationType ? { calculation_type: calculationType } : undefined },
    );
    return response.data.items || [];
  },

  async finalizeCalculation(params: {
    buildingId: string;
    calculationType: 'STANDARD' | 'HEAT_ALLOCATOR';
    period?: string | null;
    heatPeriodId?: string | null;
    exportFormat?: 'xlsx' | 'c300_csv';
  }): Promise<CalculationReportRevision> {
    const response = await apiClient.post<CalculationReportRevision>(
      `/reports/calculations/buildings/${params.buildingId}/finalize`,
      {
        calculation_type: params.calculationType,
        period: params.period || null,
        heat_period_id: params.heatPeriodId || null,
        export_format: params.exportFormat || null,
      },
    );
    return response.data;
  },

  async unlockCalculation(
    revisionId: string,
    reason: string,
  ): Promise<CalculationReportRevision> {
    const response = await apiClient.post<CalculationReportRevision>(
      `/reports/calculations/revisions/${revisionId}/unlock`,
      { reason },
    );
    return response.data;
  },

  async downloadCalculationArtifact(
    artifactId: string,
  ): Promise<{ data: Blob; fileName?: string | null }> {
    const response = await apiClient.get(
      `/reports/calculations/artifacts/${artifactId}/download`,
      { responseType: 'blob' },
    );
    return {
      data: response.data as Blob,
      fileName: parseContentDisposition(response.headers || {}),
    };
  },

  async getResidentialConsumptionReport(
    params: ResidentialConsumptionReportParams
  ): Promise<ConsumptionReportResponse> {
    const { residentialComplexId } = params;
    const response = await apiClient.get<ConsumptionReportResponse>(
      `/reports/residential-complex/${residentialComplexId}/consumption`,
      {
        params: buildParams(params),
      }
    );
    return response.data;
  },

  async downloadResidentialConsumptionReport(
    params: ResidentialConsumptionReportParams,
    exportFormat?: 'xlsx' | 'c300_csv',
  ): Promise<{ data: Blob; fileName?: string | null }> {
    const { residentialComplexId } = params;
    const response = await apiClient.get(
      `/reports/residential-complex/${residentialComplexId}/consumption.xlsx`,
      {
        params: {
          ...buildParams(params),
          ...(exportFormat ? { export_format: exportFormat } : {}),
        },
        responseType: 'blob',
      }
    );
    const fileName = parseContentDisposition(response.headers || {});
    return { data: response.data as Blob, fileName };
  },

  async getSettlementElectricityReport(
    params: SettlementElectricityReportParams
  ): Promise<SettlementElectricityReportResponse> {
    const { residentialComplexId } = params;
    const response = await apiClient.get<SettlementElectricityReportResponse>(
      `/reports/residential-complex/${residentialComplexId}/settlement-electricity`,
      { params: buildSettlementParams(params) }
    );
    return response.data;
  },

  async downloadSettlementElectricityReport(
    params: SettlementElectricityReportParams
  ): Promise<{ data: Blob; fileName?: string | null }> {
    const { residentialComplexId } = params;
    const response = await apiClient.get(
      `/reports/residential-complex/${residentialComplexId}/settlement-electricity.xlsx`,
      {
        params: buildSettlementParams(params),
        responseType: 'blob',
      }
    );
    return {
      data: response.data as Blob,
      fileName: parseContentDisposition(response.headers || {}),
    };
  },

  async getSettlementElectricityHourlyReport(
    params: SettlementElectricityHourlyReportParams
  ): Promise<SettlementElectricityHourlyReportResponse> {
    const response = await apiClient.get<SettlementElectricityHourlyReportResponse>(
      `/reports/residential-complex/${params.residentialComplexId}/settlement-electricity/hourly`,
      { params: buildSettlementHourlyParams(params) }
    );
    return response.data;
  },

  async downloadSettlementElectricityHourlyReport(
    params: SettlementElectricityHourlyReportParams
  ): Promise<{ data: Blob; fileName?: string | null }> {
    const response = await apiClient.get(
      `/reports/residential-complex/${params.residentialComplexId}/settlement-electricity/hourly.xlsx`,
      {
        params: buildSettlementHourlyParams(params),
        responseType: 'blob',
      }
    );
    return {
      data: response.data as Blob,
      fileName: parseContentDisposition(response.headers || {}),
    };
  },
};
